import React, { useState, useEffect } from 'react';
import { getTenantPermissions, updateTenantPermissions } from '../../services/firestore/permissionService';
import { ROLES, PERMISSIONS, DEFAULT_ROLE_PERMISSIONS } from '../../constants/permissions';
import { NOTIFICATION_TYPES, NOTIFICATION_LABELS } from '../../constants/notifications';
import Button from '../../components/shared/Button';
import Modal from '../../components/shared/Modal';
import { useToast } from '../../hooks/useToast';
import { logAuditAction } from '../../services/logging/auditService';
import ConfirmDialog from '../../components/shared/ConfirmDialog';

const PermissionManagement = ({ userData }) => {
    const [rolePermissions, setRolePermissions] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // UI State
    const [selectedRole, setSelectedRole] = useState(ROLES.COORDINATOR);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [ownerRoles, setOwnerRoles] = useState([]);
    const [transporterRoles, setTransporterRoles] = useState([]);
    const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [notificationPreferences, setNotificationPreferences] = useState({});
    const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);

    // Dialog States
    const [confirmConfig, setConfirmConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'warning',
        action: null,
        showCancel: true
    });

    const { success, error: toastError } = useToast();

    // System roles that cannot be deleted
    const SYSTEM_ROLES = Object.values(ROLES);

    useEffect(() => {
        loadPermissions();
    }, [userData?.tenantId]);

    const loadPermissions = async () => {
        if (!userData?.tenantId) return;
        setLoading(true);
        try {
            const data = await getTenantPermissions(userData.tenantId);
            setRolePermissions(data.rolePermissions);
            setOwnerRoles(data.ownerRoles || []);
            setTransporterRoles(data.transporterRoles || []);
            setNotificationPreferences(data.notificationPreferences || {});

            // Calculate all available roles (System defaults + Custom ones in DB)
            const dbRoles = Object.keys(data.rolePermissions);
            const systemRoles = Object.values(ROLES);
            const allRoles = Array.from(new Set([...systemRoles, ...dbRoles]));

            setAvailableRoles(allRoles);
        } catch (error) {
            console.error('Yetkiler yüklenirken hata:', error);
            setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
            setAvailableRoles(Object.values(ROLES));
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePermission = (feature, permission) => {
        // PROTECTION: Owners cannot remove Permission Management
        const isSelectedRoleOwner = ownerRoles.includes(selectedRole);
        if (isSelectedRoleOwner && feature === PERMISSIONS.PERMISSION_MANAGEMENT) {
            toastError('Owner rollerinde Yetki Yönetimi kaldırılamaz.');
            return;
        }

        // Create deep copy to avoid mutation issues
        const currentPermissions = { ...rolePermissions };
        const roleData = currentPermissions[selectedRole] || {};
        const featurePermissions = roleData[feature] || [];

        let newFeaturePermissions;
        if (featurePermissions.includes(permission)) {
            newFeaturePermissions = featurePermissions.filter(p => p !== permission);
        } else {
            newFeaturePermissions = [...featurePermissions, permission];
        }

        const updatedRolePermissions = {
            ...currentPermissions,
            [selectedRole]: {
                ...roleData,
                [feature]: newFeaturePermissions
            }
        };

        setRolePermissions(updatedRolePermissions);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateTenantPermissions(userData.tenantId, rolePermissions, ownerRoles, transporterRoles, notificationPreferences);

            // Log the action
            await logAuditAction(
                userData.tenantId,
                userData,
                'PERMISSION_UPDATE',
                'config/permissions',
                { description: `${formatRoleName(selectedRole)} rolü yetkileri güncellendi.` }
            );

            success('Yetkiler başarıyla güncellendi.');
        } catch (error) {
            toastError('Yetkiler kaydedilirken bir hata oluştu.');
        } finally {
            setSaving(false);
        }
    };

    const handleCreateRole = async () => {
        if (!newRoleName.trim()) return;

        // Simple slugify or keep as is? Let's lowercase and slugify for safety as key
        const roleKey = newRoleName.trim().toLowerCase().replace(/\s+/g, '_');

        if (availableRoles.includes(roleKey)) {
            toastError('Bu rol zaten mevcut.');
            return;
        }

        // Add to permissions object with empty permissions
        const updatedPermissions = {
            ...rolePermissions,
            [roleKey]: {}
        };

        setRolePermissions(updatedPermissions);
        setAvailableRoles([...availableRoles, roleKey]);
        setSelectedRole(roleKey);
        setNewRoleName('');
        setIsAddRoleModalOpen(false);

        // Auto save to persist the new role
        try {
            await updateTenantPermissions(userData.tenantId, updatedPermissions);

            await logAuditAction(
                userData.tenantId,
                userData,
                'ROLE_CREATE',
                'config/permissions',
                { roleName: newRoleName, roleKey: roleKey }
            );

            success(`${newRoleName} rolü oluşturuldu.`);
        } catch (err) {
            toastError('Rol oluşturulurken hata: ' + err.message);
        }
    };

    const handleDeleteRole = async () => {
        if (SYSTEM_ROLES.includes(selectedRole)) {
            toastError('Sistem rolleri silinemez.');
            return;
        }

        setConfirmConfig({
            isOpen: true,
            title: 'Rolü Sil',
            message: `${formatRoleName(selectedRole)} rolünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
            type: 'error',
            confirmText: 'Sil',
            action: async () => {
                const updatedPermissions = { ...rolePermissions };
                delete updatedPermissions[selectedRole];

                setRolePermissions(updatedPermissions);

                const newRoles = availableRoles.filter(r => r !== selectedRole);
                setAvailableRoles(newRoles);
                setSelectedRole(ROLES.COORDINATOR); // Fallback to safe default

                // Save
                try {
                    await updateTenantPermissions(userData.tenantId, updatedPermissions);

                    await logAuditAction(
                        userData.tenantId,
                        userData,
                        'ROLE_DELETE',
                        'config/permissions',
                        { roleKey: selectedRole }
                    );

                    success('Rol silindi.');
                } catch (err) {
                    toastError('Rol silinirken hata: ' + err.message);
                }
            }
        });
    };

    const handleToggleOwner = async () => {
        const isCurrentlyOwner = ownerRoles.includes(selectedRole);

        if (isCurrentlyOwner) {
            // Check if this is the last owner
            if (ownerRoles.length <= 1) {
                setConfirmConfig({
                    isOpen: true,
                    title: 'Hata',
                    message: 'Sistemde en az bir (1) Owner rolü bulunmalıdır.',
                    type: 'error',
                    showCancel: false,
                    confirmText: 'Kapat'
                });
                return;
            }

            setConfirmConfig({
                isOpen: true,
                title: 'Kısıtlama Kaldır',
                message: 'Owner yetkisi kaldırılacaktır. Emin misiniz?',
                type: 'warning',
                action: () => setOwnerRoles(prev => prev.filter(r => r !== selectedRole))
            });
        } else {
            // Mutual exclusivity check
            if (transporterRoles.includes(selectedRole)) {
                toastError('Bir rol aynı anda hem Owner hem de Tedarikçi olamaz.');
                return;
            }

            setConfirmConfig({
                isOpen: true,
                title: 'Tam Yetki Ata',
                message: 'Bu rol Owner (Tam Yetkili) olarak atanacaktır. Devam edilsin mi?',
                type: 'warning',
                action: () => {
                    // Ensure owner role has permission management before making it owner strictly
                    const currentPermissions = { ...rolePermissions };
                    const roleData = currentPermissions[selectedRole] || {};

                    const updatedPermissions = {
                        ...currentPermissions,
                        [selectedRole]: {
                            ...roleData,
                            [PERMISSIONS.PERMISSION_MANAGEMENT]: [PERMISSIONS.VIEW, PERMISSIONS.CREATE, PERMISSIONS.EDIT, PERMISSIONS.DELETE]
                        }
                    };

                    setRolePermissions(updatedPermissions);
                    setOwnerRoles(prev => [...prev, selectedRole]);
                }
            });
        }
    };

    const handleToggleTransporter = async () => {
        const isCurrentlyTransporter = transporterRoles.includes(selectedRole);

        if (isCurrentlyTransporter) {
            setConfirmConfig({
                isOpen: true,
                title: 'Tedarikçi Durumu Kaldır',
                message: 'Bu roldeki kullanıcılar için tedarikçi seçimi artık zorunlu olmayacaktır. Emin misiniz?',
                type: 'warning',
                action: () => setTransporterRoles(prev => prev.filter(r => r !== selectedRole))
            });
        } else {
            // Mutual exclusivity check
            if (ownerRoles.includes(selectedRole)) {
                toastError('Bir rol aynı anda hem Owner hem de Tedarikçi olamaz.');
                return;
            }

            setConfirmConfig({
                isOpen: true,
                title: 'Tedarikçi Olarak Belirle',
                message: 'Bu rol "Tedarikçi" olarak işaretlenecektir. Kullanıcı yönetiminde bu rol seçildiğinde Nakliyeci firması seçilmesi zorunlu olacaktır. Devam edilsin mi?',
                type: 'warning',
                action: () => setTransporterRoles(prev => [...prev, selectedRole])
            });
        }
    };

    const handleToggleNotification = (type) => {
        const currentPrefs = { ...notificationPreferences };
        const rolePrefs = currentPrefs[selectedRole] || [];

        let newRolePrefs;
        if (rolePrefs.includes(type)) {
            newRolePrefs = rolePrefs.filter(p => p !== type);
        } else {
            newRolePrefs = [...rolePrefs, type];
        }

        setNotificationPreferences({
            ...currentPrefs,
            [selectedRole]: newRolePrefs
        });
    };

    const formatRoleName = (role) => {
        if (!role) return '';
        return role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    // UI Constants
    const features = [
        { id: PERMISSIONS.DASHBOARD, label: 'Dashboard' },

        // Operasyon
        { id: PERMISSIONS.TRANSPORTERS, label: 'Tedarikçi Yönetimi' },
        { id: PERMISSIONS.PLANNING, label: 'Araç Planlama' },
        { id: PERMISSIONS.RESERVATIONS, label: 'Rezervasyon Yönetimi / Takip' },
        { id: PERMISSIONS.DOCKS, label: 'Rampa Durumu' },
        { id: PERMISSIONS.VIEW_CARRIER_PERFORMANCE, label: 'Nakliyeci Performansı' },

        // Nakliye
        { id: PERMISSIONS.VEHICLES, label: 'Araçlar' },
        { id: PERMISSIONS.DRIVERS, label: 'Sürücüler' },
        { id: PERMISSIONS.MY_RESERVATIONS, label: 'Rezervasyonlar' },

        // Tesis
        { id: PERMISSIONS.GATE_PROCESS, label: 'Hızlı Giriş/Çıkış' },
        { id: PERMISSIONS.DOCK_PROCESS, label: 'Yükleme Listesi' },

        // Yönetim
        { id: PERMISSIONS.USER_MANAGEMENT, label: 'Kullanıcı Yönetimi' },
        { id: PERMISSIONS.PERMISSION_MANAGEMENT, label: 'Yetki Yönetimi' },
        { id: PERMISSIONS.SETTINGS, label: 'Sistem Ayarları' }
    ];

    const actions = [
        { id: PERMISSIONS.VIEW, label: 'Görüntüle' },
        { id: PERMISSIONS.CREATE, label: 'Ekle' },
        { id: PERMISSIONS.EDIT, label: 'Düzenle' },
        { id: PERMISSIONS.DELETE, label: 'Sil' },
        { id: PERMISSIONS.EXECUTE, label: 'Uygula/Onayla' }
    ];

    const handleSaveNotifications = async () => {
        setSaving(true);
        try {
            await updateTenantPermissions(userData.tenantId, rolePermissions, ownerRoles, transporterRoles, notificationPreferences);

            await logAuditAction(
                userData.tenantId,
                userData,
                'NOTIFICATION_CONFIG_UPDATE',
                'config/notifications',
                {
                    role: selectedRole,
                    preferences: notificationPreferences[selectedRole]
                }
            );

            success('Bildirim tercihleri kaydedildi.');
            setIsNotificationsModalOpen(false);
        } catch (error) {
            console.error(error);
            toastError('Bildirim tercihleri kaydedilirken hata oluştu.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-xl text-center">Yükleniyor...</div>;

    return (
        <div className="container p-xl">
            {/* Header */}
            <div className="flex justify-between items-center mb-xl">
                <div>
                    <h1 style={{ color: 'var(--text-primary)', marginBottom: '4px' }}>Yetki Yönetimi</h1>
                    <p style={{ color: 'var(--text-secondary)' }}>Rolleri yapılandırın ve erişim izinlerini yönetin.</p>
                </div>
                <div className="flex gap-md">
                    <Button onClick={handleSave} loading={saving} variant="primary">
                        💾 Değişiklikleri Kaydet
                    </Button>
                </div>
            </div>

            {/* Role Selection Toolbar */}
            <div className="card-premium glow-theme p-lg mb-xl flex items-center gap-lg">
                <div className="flex-1">
                    <label className="block text-sm text-secondary mb-xs">Düzenlenecek Rol</label>
                    <div className="flex gap-md">
                        <select
                            className="glass-input flex-1"
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            style={{ padding: '10px' }}
                        >
                            {availableRoles.map(role => (
                                <option key={role} value={role}>{formatRoleName(role)}</option>
                            ))}
                        </select>
                        <Button variant="secondary" onClick={() => setIsAddRoleModalOpen(true)}>
                            ➕ Yeni Rol Ekle
                        </Button>
                        {!SYSTEM_ROLES.includes(selectedRole) && (
                            <Button variant="danger" onClick={handleDeleteRole}>
                                🗑️ Rolü Sil
                            </Button>
                        )}
                    </div>
                </div>

                {/* OWNER SWITCH */}
                <div style={{ borderLeft: '1px solid var(--border-color)', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label className="text-secondary text-xs font-bold uppercase tracking-wider">Owner Durumu</label>
                    <div className="flex items-center gap-md">
                        <div
                            onClick={handleToggleOwner}
                            style={{
                                width: '44px',
                                height: '22px',
                                background: ownerRoles.includes(selectedRole) ? '#22c55e' : '#ef4444',
                                borderRadius: '11px',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: ownerRoles.includes(selectedRole)
                                    ? '0 0 15px rgba(34, 197, 94, 0.4)'
                                    : '0 0 15px rgba(239, 68, 68, 0.4)'
                            }}
                        >
                            <div style={{
                                width: '16px',
                                height: '16px',
                                background: 'white',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '3px',
                                left: ownerRoles.includes(selectedRole) ? '25px' : '3px',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                        </div>
                        <span style={{
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: ownerRoles.includes(selectedRole) ? '#22c55e' : '#ef4444',
                            textShadow: ownerRoles.includes(selectedRole) ? '0 0 10px rgba(34, 197, 94, 0.2)' : '0 0 10px rgba(239, 68, 68, 0.2)'
                        }}>
                            {ownerRoles.includes(selectedRole) ? 'AKTİF (Tam Yetki)' : 'PASİF'}
                        </span>
                    </div>
                </div>

                {/* TRANSPORTER SWITCH */}
                <div style={{ borderLeft: '1px solid var(--border-color)', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label className="text-secondary text-xs font-bold uppercase tracking-wider">Tedarikçi Durumu</label>
                    <div className="flex items-center gap-md">
                        <div
                            onClick={handleToggleTransporter}
                            style={{
                                width: '44px',
                                height: '22px',
                                background: transporterRoles.includes(selectedRole) ? '#22c55e' : '#ef4444',
                                borderRadius: '11px',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: transporterRoles.includes(selectedRole)
                                    ? '0 0 15px rgba(34, 197, 94, 0.4)'
                                    : '0 0 15px rgba(239, 68, 68, 0.4)'
                            }}
                        >
                            <div style={{
                                width: '16px',
                                height: '16px',
                                background: 'white',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '3px',
                                left: transporterRoles.includes(selectedRole) ? '25px' : '3px',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                        </div>
                        <span style={{
                            fontSize: '13px',
                            fontWeight: 'bold',
                            color: transporterRoles.includes(selectedRole) ? '#22c55e' : '#ef4444',
                            textShadow: transporterRoles.includes(selectedRole) ? '0 0 10px rgba(34, 197, 94, 0.2)' : '0 0 10px rgba(239, 68, 68, 0.2)'
                        }}>
                            {transporterRoles.includes(selectedRole) ? 'AKTİF (Seçim Zorunlu)' : 'PASİF'}
                        </span>
                    </div>
                </div>

                {/* NOTIFICATION PREFERENCES BUTTON */}
                <div style={{ borderLeft: '1px solid var(--border-color)', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label className="text-secondary text-xs font-bold uppercase tracking-wider">Bildirim Ayarları</label>
                    <Button variant="outline" size="sm" onClick={() => setIsNotificationsModalOpen(true)}>
                        🔔 Bildirim Tercihleri
                    </Button>
                </div>
            </div>

            {/* Permissions Grid for Selected Role */}
            <div className="card-premium glow-theme animated-entry">
                <h3 className="mb-md flex items-center gap-sm" style={{ color: 'var(--text-primary)', textTransform: 'uppercase', fontSize: '14px', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' }}>
                    🔐 {formatRoleName(selectedRole)} Yetkileri
                    {rolePermissions[selectedRole] && Object.keys(rolePermissions[selectedRole]).length > 0 && (
                        <span className="text-xs text-secondary normal-case ml-auto">
                            {Object.values(rolePermissions[selectedRole]).flat().length} yetki tanımlı
                        </span>
                    )}
                </h3>

                <div className="grid grid-cols-2 gap-md mt-md">
                    {features.map(feature => (
                        <div key={feature.id} style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '12px', color: 'var(--text-primary)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {feature.label}
                            </div>
                            <div className="flex flex-wrap gap-xs">
                                {actions.map(action => {
                                    const isActive = rolePermissions[selectedRole]?.[feature.id]?.includes(action.id);
                                    return (
                                        <button
                                            key={action.id}
                                            onClick={() => handleTogglePermission(feature.id, action.id)}
                                            style={{
                                                padding: '6px 12px',
                                                fontSize: '12px',
                                                borderRadius: '6px',
                                                border: '1px solid',
                                                borderColor: isActive ? 'var(--primary-color)' : 'var(--border-color)',
                                                background: isActive ? 'rgba(59, 130, 246, 0.15)' : 'var(--bg-primary)',
                                                color: isActive ? 'var(--primary-color)' : 'var(--text-secondary)',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                boxShadow: isActive ? '0 2px 8px rgba(59, 130, 246, 0.2)' : 'none'
                                            }}
                                        >
                                            {action.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Add Role Modal */}
            <Modal
                isOpen={isAddRoleModalOpen}
                onClose={() => setIsAddRoleModalOpen(false)}
                title="Yeni Rol Oluştur"
            >
                <div className="p-md">
                    <label className="block mb-sm text-secondary">Rol Adı</label>
                    <input
                        type="text"
                        className="glass-input w-full mb-lg"
                        placeholder="Örn: Vardiya Amiri"
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        autoFocus
                    />
                    <div className="flex justify-end gap-md">
                        <Button variant="secondary" onClick={() => setIsAddRoleModalOpen(false)}>İptal</Button>
                        <Button variant="primary" onClick={handleCreateRole} disabled={!newRoleName.trim()}>Oluştur</Button>
                    </div>
                </div>
            </Modal>

            {/* Notification Preferences Modal */}
            <Modal
                isOpen={isNotificationsModalOpen}
                onClose={() => setIsNotificationsModalOpen(false)}
                title={`${formatRoleName(selectedRole)} - Bildirim Tercihleri`}
            >
                <div className="p-md">
                    <p className="text-secondary mb-lg text-sm">
                        Bu roldeki kullanıcıların hangi durum değişikliklerinde bildirim alacağını seçin.
                    </p>
                    <div className="flex flex-col gap-md">
                        {Object.entries(NOTIFICATION_LABELS).map(([type, label]) => {
                            const isActive = notificationPreferences[selectedRole]?.includes(type);
                            return (
                                <div
                                    key={type}
                                    onClick={() => handleToggleNotification(type)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '12px 16px',
                                        borderRadius: '10px',
                                        background: isActive ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-secondary)',
                                        border: '1px solid',
                                        borderColor: isActive ? 'var(--primary-color)' : 'var(--border-color)',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '4px',
                                        border: '2px solid',
                                        borderColor: isActive ? 'var(--primary-color)' : 'var(--text-secondary)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: isActive ? 'var(--primary-color)' : 'transparent',
                                        color: 'white'
                                    }}>
                                        {isActive && <span className="material-icons" style={{ fontSize: '14px' }}>check</span>}
                                    </div>
                                    <span style={{ color: 'var(--text-primary)', fontWeight: isActive ? 'bold' : 'normal' }}>
                                        {label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex justify-end mt-xl">
                        <Button variant="secondary" onClick={() => setIsNotificationsModalOpen(false)} className="mr-2">
                            İptal
                        </Button>
                        <Button variant="primary" onClick={handleSaveNotifications} loading={saving}>
                            Kaydet
                        </Button>
                    </div>
                </div >
            </Modal >


            {/* Premium Confirm Dialog */}
            < ConfirmDialog
                {...confirmConfig}
                onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={async () => {
                    if (confirmConfig.action) await confirmConfig.action();
                    setConfirmConfig(prev => ({ ...prev, isOpen: false }));
                }}
            />
        </div >
    );
};

export default PermissionManagement;
